import type { BaseEditor } from 'slate';

// Composes multiple plugins type-safe into a single one.
export function composePlugins<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Plugins extends OptionalEditorWrapperFunc<any, any>[],
>(...plugins: Plugins): WrapperFuncPlugin<ComposeWrapperFuncs<Plugins>> {
  return plugins.filter(Boolean).reduce(
    (composed, plugin) => editor =>
      (plugin as EditorWrapperFunc)((composed as EditorWrapperFunc)(editor)),
    editor => editor
  ) as WrapperFuncPlugin<ComposeWrapperFuncs<Plugins>>;
}

type Plugin<
  RequiredEditor extends BaseEditor = BaseEditor,
  PluginEditor extends BaseEditor = BaseEditor,
> = <E extends RequiredEditor = RequiredEditor>(e: E) => E & PluginEditor;

// Only used internally to infer types, for external definitions use the Plugin type instead
type EditorWrapperFunc<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  RequiredEditor extends BaseEditor = any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  WrappedEditor extends RequiredEditor = any,
> = (e: RequiredEditor) => WrappedEditor;

// Only used internally to infer types, for external definitions use the Plugin type instead
type OptionalEditorWrapperFunc<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  RequiredEditor extends BaseEditor = any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  WrappedEditor extends RequiredEditor = any,
> = EditorWrapperFunc<RequiredEditor, WrappedEditor> | false | undefined | null;

type ComposeWrapperFuncs<Plugins extends OptionalEditorWrapperFunc[]> = Plugins extends [
  plugin: infer BasePlugin,
  plugin: infer NextPlugin,
  ...plugins: infer RemainingPlugins,
]
  ? BasePlugin extends EditorWrapperFunc<infer BaseEditor, infer BaseWrappedEditor>
    ? NextPlugin extends EditorWrapperFunc<infer RequiredEditor, infer WrappedEditor>
      ? ComposeWrapperFuncs<
          [
            EditorWrapperFunc<
              Omit<RequiredEditor, keyof BaseWrappedEditor> & BaseEditor,
              BaseWrappedEditor &
                WrappedEditor &
                Omit<RequiredEditor, keyof BaseWrappedEditor> &
                BaseEditor
            >,
            ...(RemainingPlugins extends OptionalEditorWrapperFunc[] ? RemainingPlugins : []),
          ]
        >
      : ComposeWrapperFuncs<
          [
            BasePlugin,
            ...(RemainingPlugins extends OptionalEditorWrapperFunc[] ? RemainingPlugins : []),
          ]
        >
    : ComposeWrapperFuncs<
        [
          NextPlugin extends OptionalEditorWrapperFunc ? NextPlugin : false,
          ...(RemainingPlugins extends OptionalEditorWrapperFunc[] ? RemainingPlugins : []),
        ]
      >
  : Plugins[0] extends EditorWrapperFunc
    ? Plugins[0]
    : never;

type WrapperFuncPlugin<WrapperFunc extends EditorWrapperFunc> =
  WrapperFunc extends EditorWrapperFunc<infer Required, infer Provided>
    ? Plugin<Required, Provided>
    : never;
